import React, { useEffect, useMemo, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { getAccount } from '../auth/authService';
import { dvList } from '../api/dataverseClient';
import { Project } from '../types';
import { ProjectMemberRole } from '../services/projectMemberService';
import {
  getTenantPool, listPoolMembers, ResourcePool, ResourcePoolMember, DEFAULT_HOURS_PER_DAY,
} from '../services/resourcePoolService';
import {
  getPoolLinkedProjects, getWorkloadTasks, computeWorkload, monFriWorkingDay,
  WorkloadTask, MemberWorkload, BucketCell, WorkCapacity,
} from '../services/resourceWorkloadService';

interface Props { pca: IPublicClientApplication }

const DAY_MS = 86400000;
export const WEEK_COL_W = 66;
export const NAME_COL_W = 200;

/**
 * PM-and-above gate for the portfolio workload view. There is no
 * tenant-level role helper in this app (roles are per-project), so we
 * approximate "PM and above": the pool owner, OR anyone who is a
 * ProjectManager on at least one project, passes.
 *
 * DENY BY DEFAULT: a user with zero projectmember rows (no role anywhere)
 * is not allowed through. Only a query failure stays lenient so a transient
 * error doesn't lock out a permissioned user.
 */
async function canViewWorkload(pca: IPublicClientApplication, oid: string, pool: ResourcePool | null): Promise<boolean> {
  if (pool && pool.dagrun_pm_ownerid === oid) return true;
  try {
    const anyMembership = await dvList<{ dagrun_pm_projectmemberid: string; dagrun_pm_role: number | null }>(
      pca, 'dagrun_pm_projectmembers', {
        select: ['dagrun_pm_projectmemberid', 'dagrun_pm_role'],
        filter: `dagrun_pm_userid eq '${oid}'`, top: 50,
      });
    if (anyMembership.length === 0) return false; // no role anywhere → deny
    return anyMembership.some(m => m.dagrun_pm_role === ProjectMemberRole.ProjectManager);
  } catch {
    return true; // query failed → don't lock out a permissioned user
  }
}

function isoDay(d: Date): string { return d.toISOString().slice(0, 10); }

/** Utilization → cell colour. Green under, amber near, red over/conflict. */
export function cellColors(cell: BucketCell): { bg: string; fg: string } {
  if (cell.capacity === 0 && cell.hours === 0) return { bg: 'transparent', fg: 'var(--ink-5)' };
  if (cell.conflict) return { bg: '#DC2626', fg: '#fff' };
  if (cell.overallocated) return { bg: '#F87171', fg: '#fff' };
  const u = cell.utilization;
  if (u >= 0.85) return { bg: '#FDE68A', fg: '#92400E' };   // near capacity
  if (u >= 0.4)  return { bg: '#BBF7D0', fg: '#065F46' };   // healthy load
  if (u > 0)     return { bg: '#DCFCE7', fg: '#166534' };   // light
  return { bg: 'var(--surface-2)', fg: 'var(--ink-5)' };
}

export default function ResourceWorkloadView({ pca }: Props) {
  const [pool, setPool] = useState<ResourcePool | null>(null);
  const [members, setMembers] = useState<ResourcePoolMember[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [tasks, setTasks] = useState<WorkloadTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState<boolean | null>(null);

  // Filters.
  const today = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => isoDay(today), [today]);
  const defaultEnd = useMemo(() => isoDay(new Date(today.getTime() + 84 * DAY_MS)), [today]); // ~12 weeks
  const [rangeStart, setRangeStart] = useState(defaultStart);
  const [rangeEnd, setRangeEnd] = useState(defaultEnd);
  const [memberFilter, setMemberFilter] = useState('');   // '' = all
  const [projectFilter, setProjectFilter] = useState(''); // '' = all

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const acct = await getAccount(pca);
        const oid = acct?.localAccountId ?? acct?.homeAccountId?.split('.')[0] ?? '';
        const p = await getTenantPool(pca);
        if (cancelled) return;
        setPool(p);
        const ok = await canViewWorkload(pca, oid, p);
        if (cancelled) return;
        setAuthorized(ok);
        if (!p || !ok) { setLoading(false); return; }
        const [mem, linked] = await Promise.all([
          listPoolMembers(pca, p.dagrun_pm_resourcepoolid),
          getPoolLinkedProjects(pca),
        ]);
        if (cancelled) return;
        setMembers(mem);
        setProjects(linked);
        const t = await getWorkloadTasks(pca, linked.map(x => x.dagrun_pm_projectid));
        if (cancelled) return;
        setTasks(t);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pca]);

  const workload: MemberWorkload[] = useMemo(() => {
    const start = new Date(`${rangeStart}T00:00:00Z`);
    const end = new Date(`${rangeEnd}T00:00:00Z`);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return [];
    // Portfolio capacity = each pool member's default hours/day on a Mon–Fri
    // week (unchanged behaviour; the project Resources tab injects work calendars).
    const byId = new Map(members.map(m => [m.dagrun_pm_userid ?? '', m]));
    const capacityFor = (uid: string): WorkCapacity => ({
      isWorkingDay: monFriWorkingDay,
      hoursPerDay: byId.get(uid)?.dagrun_pm_defaulthoursperday ?? DEFAULT_HOURS_PER_DAY,
    });
    return computeWorkload(members, tasks, {
      memberUserId: memberFilter || null,
      projectId: projectFilter || null,
      rangeStart: start, rangeEnd: end,
    }, capacityFor);
  }, [members, tasks, memberFilter, projectFilter, rangeStart, rangeEnd]);

  const weekCount = workload[0]?.buckets.length ?? 0;
  const conflictCount = workload.filter(w => w.hasConflict).length;
  const overCount = workload.filter(w => w.hasOverallocation).length;

  if (loading) return <div className="spinner" style={{ margin: '40px auto' }} />;

  if (authorized === false) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
        The resource workload view is available to project managers and above.
      </div>
    );
  }
  if (!pool) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
        No global resource pool is configured. Set one up in Admin to see cross-project workload.
      </div>
    );
  }
  if (projects.length === 0) {
    return (
      <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)' }}>
        No projects are linked to the resource pool yet. Link projects from Project Settings → Resources.
      </div>
    );
  }

  return (
    <div>
      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14, marginBottom: 16 }}>
        <Field label="Resource">
          <select value={memberFilter} onChange={e => setMemberFilter(e.target.value)} style={selectStyle}>
            <option value="">All resources</option>
            {members.map(m => (
              <option key={m.dagrun_pm_resourcepoolmemberid} value={m.dagrun_pm_userid ?? ''}>
                {m.dagrun_pm_username ?? m.dagrun_pm_name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Project">
          <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)} style={selectStyle}>
            <option value="">All pool projects</option>
            {projects.map(p => (
              <option key={p.dagrun_pm_projectid} value={p.dagrun_pm_projectid}>{p.dagrun_pm_name}</option>
            ))}
          </select>
        </Field>
        <Field label="From">
          <input type="date" value={rangeStart} onChange={e => setRangeStart(e.target.value)} style={selectStyle} />
        </Field>
        <Field label="To">
          <input type="date" value={rangeEnd} onChange={e => setRangeEnd(e.target.value)} style={selectStyle} />
        </Field>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--ink-3)', alignItems: 'center' }}>
          {overCount > 0 && <span><span style={{ color: '#F87171' }}>■</span> {overCount} over-allocated</span>}
          {conflictCount > 0 && <span><span style={{ color: '#DC2626' }}>■</span> {conflictCount} in conflict</span>}
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 11, color: 'var(--ink-4)', flexWrap: 'wrap' }}>
        <LegendSwatch bg="#DCFCE7" label="Light" />
        <LegendSwatch bg="#BBF7D0" label="Healthy" />
        <LegendSwatch bg="#FDE68A" label="Near capacity" />
        <LegendSwatch bg="#F87171" label="Over-allocated" />
        <LegendSwatch bg="#DC2626" label="Conflict (2+ projects >100%)" />
        <span style={{ marginLeft: 'auto' }}>Weekly hours vs. capacity across pool-linked projects</span>
      </div>

      {/* Timeline grid */}
      {workload.length === 0 || weekCount === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--ink-4)' }}>
          No allocation in this range. Tasks need an assignee, an estimate, and start + due dates.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={{ ...headCell, position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', minWidth: NAME_COL_W, textAlign: 'left' }}>Resource</th>
                {workload[0].buckets.map(b => (
                  <th key={b.startMs} style={{ ...headCell, minWidth: WEEK_COL_W }}>
                    {new Date(b.startMs).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {workload.map(row => (
                <tr key={row.userId || row.name}>
                  <td style={{ ...nameCell, position: 'sticky', left: 0, zIndex: 1, background: 'var(--surface)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 500, color: 'var(--ink)' }}>{row.name}</span>
                      {row.hasConflict && <span title="Cross-project conflict" style={{ color: '#DC2626' }}>⚠</span>}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--ink-4)', fontFamily: 'var(--font-mono)' }}>
                      {Math.round(row.totalHours)}h · {row.hoursPerDay}h/day
                    </div>
                  </td>
                  {row.buckets.map(cell => {
                    const { bg, fg } = cellColors(cell);
                    const util = cell.capacity > 0 ? Math.round(cell.utilization * 100) : 0;
                    const projNames = cell.projectIds
                      .map(id => projects.find(p => p.dagrun_pm_projectid === id)?.dagrun_pm_name ?? '—')
                      .join(', ');
                    const title = cell.hours === 0
                      ? 'No allocation'
                      : `${Math.round(cell.hours)}h / ${Math.round(cell.capacity)}h capacity (${util}%)`
                        + `\nProjects: ${projNames || '—'}`
                        + (cell.conflict ? '\n⚠ Over 100% across 2+ projects' : cell.overallocated ? '\nOver-allocated' : '');
                    return (
                      <td key={cell.startMs} title={title}
                        style={{ ...dataCell, background: bg, color: fg }}>
                        {cell.hours > 0 ? (
                          <span style={{ fontSize: 11, fontWeight: 600 }}>
                            {util > 0 ? `${util}%` : `${Math.round(cell.hours)}h`}
                            {cell.conflict && ' ⚠'}
                          </span>
                        ) : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 12, padding: '5px 8px',
  border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', background: '#fff', color: 'var(--ink)',
};
const headCell: React.CSSProperties = {
  padding: '8px 6px', fontSize: 10, fontWeight: 600, color: 'var(--ink-3)',
  textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid var(--border)', textAlign: 'center',
};
const nameCell: React.CSSProperties = {
  padding: '8px 12px', borderBottom: '0.5px solid var(--border-light)', minWidth: NAME_COL_W, fontSize: 13,
};
const dataCell: React.CSSProperties = {
  padding: '8px 4px', borderBottom: '0.5px solid var(--border-light)', borderLeft: '0.5px solid var(--border-light)',
  textAlign: 'center', verticalAlign: 'middle', minWidth: WEEK_COL_W,
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, color: 'var(--ink-4)', fontFamily: 'var(--font-ui)' }}>{label}</span>
      {children}
    </label>
  );
}
function LegendSwatch({ bg, label }: { bg: string; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: bg, border: '0.5px solid var(--border)' }} />
      {label}
    </span>
  );
}
